import { format } from 'date-fns'

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/
const UTC_DATE_BOUNDARY_REGEX = /^\d{4}-\d{2}-\d{2}T(?:00:00:00(?:\.000)?|23:59:59(?:\.999)?)Z$/

function parseStrictDateOnly(dateOnly: string): Date {
  const [year, month, day] = dateOnly.split('-').map(Number)
  const parsedDate = new Date(year, month - 1, day)

  // Reject impossible calendar dates such as 2024-02-31.
  if (
    parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== month - 1
    || parsedDate.getDate() !== day
  ) {
    return new Date(Number.NaN)
  }

  return parsedDate
}

/**
 * Checks if a string is a date-only format (YYYY-MM-DD)
 */
export function isDateOnlyString(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY_REGEX.test(value)
}

/**
 * Formats a Date object to a date-only string (YYYY-MM-DD).
 * Uses local date components to avoid timezone drift.
 */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Parses a date string into a Date object using local time components.
 * For date-only strings (YYYY-MM-DD), constructs directly from components.
 * For ISO datetime strings, extracts the date portion first to avoid timezone drift.
 */
export function parseDateOnly(value: string): Date {
  if (DATE_ONLY_REGEX.test(value)) {
    return parseStrictDateOnly(value)
  }
  // For ISO datetime strings like "2026-01-23T00:00:00Z", extract date portion
  if (value.length >= 10) {
    const datePart = value.slice(0, 10)
    if (DATE_ONLY_REGEX.test(datePart)) {
      return parseStrictDateOnly(datePart)
    }
  }
  return new Date(value)
}

/**
 * Converts a date-only string to UTC end-of-day ISO string for API boundaries.
 * The end-of-day is 23:59:59.999 UTC on the selected date.
 */
export function dateOnlyToUtcEndOfDay(dateOnly: string): string {
  if (!isDateOnlyString(dateOnly)) {
    throw new Error(`Expected date-only string (YYYY-MM-DD), got: ${dateOnly}`)
  }
  if (Number.isNaN(parseDateOnly(dateOnly).getTime())) {
    throw new Error(`Expected valid date-only string (YYYY-MM-DD), got: ${dateOnly}`)
  }
  return `${dateOnly}T23:59:59.999Z`
}

/**
 * Converts a date-only string to UTC start-of-day ISO string for API boundaries.
 * The start-of-day is 00:00:00.000 UTC on the selected date.
 */
export function dateOnlyToUtcStartOfDay(dateOnly: string): string {
  if (!isDateOnlyString(dateOnly)) {
    throw new Error(`Expected date-only string (YYYY-MM-DD), got: ${dateOnly}`)
  }
  if (Number.isNaN(parseDateOnly(dateOnly).getTime())) {
    throw new Error(`Expected valid date-only string (YYYY-MM-DD), got: ${dateOnly}`)
  }
  return `${dateOnly}T00:00:00.000Z`
}

/**
 * Extracts the date-only portion (YYYY-MM-DD) from an ISO datetime string or Date.
 * For ISO strings, extracts directly from the string to preserve the intended date.
 * For Date objects, uses local date components.
 */
export function toDateOnly(value: string | Date): string {
  if (value instanceof Date) {
    return formatDateOnly(value)
  }
  if (DATE_ONLY_REGEX.test(value)) {
    if (Number.isNaN(parseDateOnly(value).getTime())) {
      throw new Error(`Invalid date value: ${value}`)
    }
    return value
  }
  if (value.length >= 10) {
    const datePart = value.slice(0, 10)
    if (DATE_ONLY_REGEX.test(datePart)) {
      const parsedDate = parseDateOnly(datePart)
      if (Number.isNaN(parsedDate.getTime())) {
        throw new Error(`Invalid date value: ${value}`)
      }
      return datePart
    }
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`Invalid date value: ${value}`)
  }
  return formatDateOnly(parsed)
}

function isDateOnlySemanticDisplayValue(value: string): boolean {
  return isDateOnlyString(value) || UTC_DATE_BOUNDARY_REGEX.test(value)
}

function parseDisplayDate(value: string): Date {
  return isDateOnlySemanticDisplayValue(value) ? parseDateOnly(value) : new Date(value)
}

function parseLocaleDisplayDate(
  value: string,
  options?: Intl.DateTimeFormatOptions,
): Date {
  if (!isDateOnlySemanticDisplayValue(value)) {
    return new Date(value)
  }

  // When a fixed target timezone is provided (ex: UTC in reports/tests),
  // anchor date-only values to UTC midnight to preserve the intended day.
  if (options?.timeZone) {
    return new Date(`${value.slice(0, 10)}T00:00:00.000Z`)
  }

  return parseDateOnly(value)
}

export function formatDate(date: string, formatString = 'LLL dd, yyyy'): string {
  if (!date) {
    throw new Error('Date is required')
  }

  // Use a constant for the sentinel unknown date value
  const UNKNOWN_DATE = '0001-01-01T00:00:00Z'
  if (date === UNKNOWN_DATE) {
    return 'Unknown'
  }

  // Preserve calendar-day semantics for date-only and UTC date-boundary values.
  const parsedDate = parseDisplayDate(date)
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Not Available'
  }

  return format(parsedDate, formatString)
}

export function isValidDateString(dateString: string) {
  const regex = /^[A-Za-z]{3} [A-Za-z]{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} GMT[-+]\d{4} \(.+\)$/

  return regex.test(dateString)
}

export function convertTimeToMinutes(time: string): number {
  const parts = time.split(':')
  if (parts.length !== 3)
    throw new Error('Invalid time format. Expected hh:mm:ss')

  const [hours, minutes, seconds] = parts.map(Number)
  return hours * 60 + minutes + seconds / 60
}

export function isISODateString(str: string) {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/

  if (!isoDateRegex.test(str))
    return false

  const date = new Date(str)
  return !Number.isNaN(date.getTime())
}

export function isISODateStringFlexible(str: string): boolean {
  if (typeof str !== 'string') return false
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/
  if (!isoDateRegex.test(str)) return false
  return !Number.isNaN(new Date(str).getTime())
}

export function formatDateLocale(
  dateStr: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!dateStr) return ''
  if (dateStr === '0001-01-01T00:00:00Z' || dateStr.startsWith('0001-01-01')) {
    return 'Unknown'
  }
  const date = parseLocaleDisplayDate(dateStr, options)
  if (Number.isNaN(date.getTime())) return 'Not Available'

  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }
  return new Intl.DateTimeFormat(undefined, options ?? defaultOptions).format(date)
}

export function findFirstDateColumn(
  columns: string[],
  sampleRow: Record<string, unknown>,
): string | undefined {
  return columns.find((col) => {
    const val = sampleRow[col]
    return typeof val === 'string' && isISODateStringFlexible(val)
  })
}
